import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { fetchIncidentCategories } from "../services/data";
import { submitIncident } from "../services/pms";
import { useAuth } from "../context/AuthContext";
import LoadingScreen from "../components/LoadingScreen";
import DenyAccessPage from "../components/DenyAccessPage";
import { useLanguage } from "../context/LanguageContext";
import Popup from "../components/Popup";
import { fetchWisdomRelatedSchools, fetchWisdomRelatedStudents } from "services/wisdom/wisdomData";

const SchoolIncident = () => {
  const urlLocation = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [schools, setSchools] = useState([]);
  const [students, setStudents] = useState([]);
  const [comment, setComment] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userInfo } = useAuth();
  const { language } = useLanguage();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const navigate = useNavigate();

  // Users who pick the school themselves; everyone else submits for their own organization.
  const canPickSchool = userInfo?.user_role === "Operations Excellence Lead" || userInfo?.user_role === "Super Admin";
  const effectiveSchoolId = canPickSchool ? schoolId : userInfo?.organization_id;
  const schoolStudents = effectiveSchoolId
    ? students.filter((student) => String(student.school_id) === String(effectiveSchoolId))
    : [];

  const handleSchoolChange = (e) => {
    setSchoolId(e.target.value);
    setSelectedStudent(null);
  };

  const handleSubCategoryClick = () => {
    if (!selectedCategory) {
      toast.error("Please select a category first!");
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedCategories(categories[e.target.value].incidentSubCategories);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleLocationChange = (event) => {
    setLocation(event.target.value);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleDateChange = (event) => {
    setDate(event.target.value);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!subCategory || !effectiveSchoolId) {
      return toast.error(language ? "Please fill all required fields" : "الرجاء ملء جميع الحقول المطلوبة");
    }

    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("sub_category", subCategory);
    formData.append("school_id", effectiveSchoolId);
    formData.append("comment", comment);
    formData.append("location", location);
    formData.append("incident_date", date);
    if (selectedStudent) formData.append("user_id", selectedStudent);

    try {
      await submitIncident(formData);
      setFile(null);
      setSubCategory("");
      setSchoolId("");
      toast.success(language ? "Incident has been submitted" : "تم تسجيل الحادثة");
      setSubmitted(true);
    } catch (error) {
      console.error("Upload error", error);
      toast.error(error.response?.data?.error || error.response?.data?.message || (language ? "Failed to submit incident" : "فشل تسجيل الحادثة"));
    }
  };

  useEffect(() => {
    const loadingOrg = async () => {
      try {
        const response = await fetchWisdomRelatedSchools();
        setSchools(response);
      } catch (error) {
        console.error("no files", error);
      }
    };

    const loadCategories = async () => {
      try {
        const response = await fetchIncidentCategories();
        setCategories(response);
      } catch (err) {
        console.error("API Error:", err);
        setError(err);
      }
    };

    const loadingStudents = async () => {
      try {
        const response = await fetchWisdomRelatedStudents();
        setStudents(response);
      } catch (error) {
        console.error("no files", error);
      }
    };

    loadingOrg();
    loadCategories();
    loadingStudents();
    setLoading(false);
  }, []);

  const closePopup = () => {
    setSubmitted(false)
    navigate('/pms');
  };

  if (loading) return <LoadingScreen />;
  if (error?.status === 403) return <Navigate to="/login" state={{ from: urlLocation }} replace />;
  if (error) return <p>Error: {error.message}</p>;
  if (userInfo.user_role !== "Operations Excellence Lead" && userInfo.user_role !== "Supervisor" && userInfo.user_role !== "Social Worker" && userInfo.user_role !== "Super Admin") return <DenyAccessPage homePage='/pms' />;
  if (userInfo?.code === 1452 || userInfo?.code === 1476) return <DenyAccessPage homePage='/watoms/dashboard' />;
  if (userInfo?.code === 1475) return <DenyAccessPage homePage='/watoms/news' />;
  if (userInfo?.code === 1310) return <DenyAccessPage homePage='/wisdom/dashboard' />;

  return (
    <div className="bg-gray-500 h-[115vh]">
      <Navbar upload={true}></Navbar>
      <form onSubmit={upload} className="assignForm form2 bg-slate-600">
        <h1 className="text-2xl font-bold text-white">{language ? "School Incident" : "حوادث المدرسة"}</h1>
        {canPickSchool ?
          <div className={language ? "text-start" : "text-end"}>
            <label className="text-white">{language ? "School:" : ":المدرسة"}</label>
            <select onChange={handleSchoolChange} className={!language && "text-end"}>
              <option value="" disabled selected>
                {language ? "Please Select a school" : "الرجاء اختيار مدرسة"}
              </option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>
          : null}
        <div className="select-group" dir={language ? "ltr" : "rtl"}>
          <div className={`select`}>
            <label className="text-white w-full">{language ? "Main Category:" : "التصنيف الرئيسي:"}</label>
            <select onChange={handleCategoryChange}>
              <option value="" disabled selected>
                {language ? "Please Select a Category" : "الرجاء اختيار تصنيف"}
              </option>
              {categories.map((category, index) => (
                <option key={index} value={index}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className={`select`}>
            <label className="text-white w-full">{language ? "Sub-Category:" : "التصنيف الفرعي:"}</label>
            <select
              onClick={handleSubCategoryClick}
              onChange={(e) => setSubCategory(e.target.value)}
            >
              <option value="" disabled selected>
                {language ? "Please Select a Sub-Category" : "الرجاء اختيار تصنيف فرعي"}
              </option>
              {selectedCategories.map((subCategory) => (
                <option key={subCategory.id} value={subCategory.id}>
                  {subCategory.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="select-group" dir={language ? "ltr" : "rtl"}>
          <div className={`select`}>
            <label className="text-white w-full">{language ? "Date:" : "تاريخ:"}</label>
            <input type="date" value={date} onChange={handleDateChange} />
          </div>
          {selectedCategory === "1" && <div className={`select`}>
            <label className="text-white w-full">{language ? "Student:" : "الطالب:"}</label>
            <select value={selectedStudent ?? ""} onChange={(e) => setSelectedStudent(e.target.value)} >
              <option value="" disabled>
                {!effectiveSchoolId
                  ? (language ? "Please select a school first" : "الرجاء اختيار مدرسة أولاً")
                  : (language ? "Please Select a Student" : "الرجاء اختيار طالب")}
              </option>
              {schoolStudents.map((student) => (
                <option key={student.user_id} value={student.user_id}>
                  {student.first_name} {student.middle_name} {student.last_name}
                </option>
              ))}
            </select>
          </div>}
        </div>
        <label htmlFor="comment" className={`w-full text-white ${!language && "text-end"}`}>{language ? "Comment:" : ":تعليق"}</label>
        <input type="text" name="comment" id="comment" className={!language && "text-end"} onChange={handleCommentChange} />
        <label htmlFor="location" className={`w-full text-white ${!language && "text-end"}`}>{language ? "Location:" : ":موقع الحادث"}</label>
        <input type="text" name="location" id="location" className={!language && "text-end"} onChange={handleLocationChange} />
        <label className={`w-full text-white ${!language && "text-end"}`}>{language ? "Attach File (optional):" : ":(اختياري) رفع ملف"}</label>
        <input type="file" name="file" className="bg-white" onChange={handleFileChange} />
        <button className="bg-wisdomOrange hover:bg-wisdomDarkOrange text-white p-2 rounded">{language ? "Submit" : "ارسال"}</button>
      </form>
      <Popup
        isOpen={submitted}
        onClose={closePopup}
        message={language ? "Incident has been submitted successfully" : "تم تسجيل الحادثة بنجاح"}
      />
    </div>
  );
};

export default SchoolIncident;
